import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AppHeader,
    DataList,
    Format,
    ListItemElement,
    RoundButton, Searchbox,
    Spinner,
    Tooltip
} from "@tag/tag-components-react-v4";
import {
    ArrowRewindLined,
    ClockFilled,
    DeleteLined,
    DesktopFilled,
    FilingCabinetFilled,
    SearchFilled,
    UserMaleLined
} from "@tag/tag-icons";
import CustomerService from "../services/CustomerService";
import {APPTermination} from "../types/APPTermination";
import UnlockConfirmationModal from "./UnlockConfirmationModal";
import styles from "../CustomerList/CustomerList.module.scss";
import AppRolesService from "../../General/services/AppRolesService";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import HelperMethods from "../../General/services/HelperMethods";
import {ACPPTermination} from "../../ACPP/types/ACPPTermination";
import { ProjectAccents } from "../../General/config/projectAccents";

function Terminations() {

    const [allTerminations, setAllTerminations] = useState<APPTermination[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedTermination, setSelectedTermination] = useState<APPTermination|undefined>();
    const [alert, setAlert] = useState<{type: any, message: string}>();
    const [searchTerm, setSearchTerm] = useState<string>('');

    const { accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    let canLockEnv = AppRolesService.canUserLockAppEnvironments(accounts, isAuthenticated);
    
    useEffect(() => {
        loadCustomerData()
    }, []);

    const getTerminationsListData = (): APPTermination[] => {
        if (!searchTerm) {
            return allTerminations;
        }

        return  HelperMethods.advancedFuseSearch(searchTerm, allTerminations, ['clientID', 'customerName', 'appGroupName', 'corporateSiteName']);
    }

    const terminationsData = useMemo(
        () => getTerminationsListData(),
        [searchTerm, allTerminations]
    );

    const loadCustomerData = () => {
        setLoading(true);
        CustomerService.getAppCustomerTerminations().then(n => {
            const updatedList = n.map((item: APPTermination) => {
                return {...item, initials: HelperMethods.getInitials(item.customerName), primaryFieldValue: `${item.customerName} (${item.clientID} - ${item.appGroupName})`};
            });
            setAllTerminations(updatedList);
            setLoading(false);
        })
    }
    
    const unlockCustomer = () => {
        setLoading(true);
        
        if (!canLockEnv || !selectedTermination) {
            throw new Error()
        }
        
        CustomerService.unlockAppCustomer(selectedTermination.appGroupName, selectedTermination?.rowKey)
            .then((response) => {
                setAlert({type: 'success', message: 'Customer restored successfully'});
            })
            .catch((response) => {
                setAlert({type: 'error', message: 'An error has occured, please contact HSC Care DevOps'});
            })
            .finally(() => {
                setLoading(false)
                loadCustomerData();
            })
    }

    let content;
    if (!allTerminations && loading) {
        content = <Spinner full/>
    }
    else if (!allTerminations.length) {
        content = <>
            <h3>There are no terminations</h3>
        </>
    }
    else {
        content = <>
            <h1>Terminations</h1>
            <h4>Customers will be terminated after 2 weeks from the date they were locked</h4>
            <AppHeader
                heading="Organisation List"
                icon={<SearchFilled/>}
                accent={ProjectAccents.PeoplePlanner}
            >
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Searchbox
                        onSearch={(e) => setSearchTerm(e)}
                        clearButton
                        style={{width: '100%'}}
                    />
                </div>
            </AppHeader>
            <DataList
                avatarProps={{srcField: 'image', placeholderField: 'initials'}}
                primaryField="primaryFieldValue"
                renderSubItemElements={(dataItem) => (
                    <>
                        {
                            dataItem.corporateSiteName ?
                                <ListItemElement
                                    icon={<FilingCabinetFilled accent={ProjectAccents.PeoplePlanner}/>}
                                    label="Corporate site:"
                                >
                                    {dataItem.corporateSiteName}
                                </ListItemElement> : undefined
                        }
                        <ListItemElement icon={<ClockFilled accent={ProjectAccents.PeoplePlanner} />} label="Locked at:">
                            <Format value={dataItem.timestampLockedUtc} dataType="datetime" />
                        </ListItemElement>
                        <ListItemElement icon={<UserMaleLined accent={ProjectAccents.PeoplePlanner} />} label={'Locked by:'}>{dataItem.requesterName}</ListItemElement>
                        <ListItemElement icon={<DesktopFilled accent={ProjectAccents.PeoplePlanner} />} label={'Salesforce: '}>{dataItem.salesForceTicketId}</ListItemElement>
                        {
                            dataItem.timestampDeletedUtc ?
                                <ListItemElement icon={<DeleteLined accent={ProjectAccents.PeoplePlanner}/>} label="Deleted:">
                                    <Format value={dataItem.timestampDeletedUtc} dataType="datetime"/>
                                </ListItemElement> : undefined
                        }
                    </>
                )}
                renderRightElements={(dataItem) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Tooltip
                            background={'dark'}
                            placement={'left'}
                            tooltipFor={
                                <RoundButton
                                    disabled={!!dataItem.timestampDeletedUtc}
                                    onClick={() => setSelectedTermination(dataItem)}
                                    icon={<ArrowRewindLined />}
                                />
                            }
                        >
                            {
                                !dataItem.timestampDeletedUtc 
                                    ? "Restore environment" 
                                    : "Environment already deleted"
                            }
                            
                        </Tooltip>
                    </div>
                )}
                data={terminationsData}
            />
        </>
    }

    return (
        <>
            {content}

            {selectedTermination ?
                <UnlockConfirmationModal
                    selectedTermination={selectedTermination}
                    handleClose={() => {
                        setSelectedTermination(undefined)
                    }}
                    handleUnlock={unlockCustomer}
                    visible={!!selectedTermination}
                />:undefined
            }

            {alert ?
                <Alert
                    className={styles.CustomerListAlert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }

            {loading ? <Spinner full /> : undefined}
        </>
    )

}

export default Terminations;
